import { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "@tanstack/react-router"
import { Layout, Menu, Drawer, useTheme } from "@mimber/ui-web"
import { OrganizationSelector } from "../OrganizationSelector"
import {
  ROUTE_TO_KEY_MAP,
  NAVIGATION_ITEMS,
  filterNavigationByFeatureToggles,
  filterNavigationByPermissions,
  getMenuKeyFromPathname,
} from "./NavigationPanel.utils"
import { useFeatureToggles } from "@/core/contexts"
import { useOrganizationPermissions } from "@/core/hooks"
import { useIsMobileViewport } from "@mimber/ui-web"
import type { NavigationPanelProps } from "./NavigationPanel.types"

const { Sider } = Layout

export const NavigationPanel: React.FunctionComponent<NavigationPanelProps> = ({
  mobileOpen = false,
  onMobileClose,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const featureToggles = useFeatureToggles()
  const { hasPermission } = useOrganizationPermissions()
  const isMobile = useIsMobileViewport()
  const { isDark } = useTheme()

  // Initialize collapsed state from localStorage
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem("sidebar-collapsed")
    if (!stored) return false

    try {
      return JSON.parse(stored)
    } catch {
      localStorage.removeItem("sidebar-collapsed")
      return false
    }
  })

  // Filter navigation items based on feature toggles and permissions
  const filteredItems = useMemo(() => {
    const featureFiltered = filterNavigationByFeatureToggles(
      NAVIGATION_ITEMS,
      featureToggles
    )
    return filterNavigationByPermissions(featureFiltered, hasPermission)
  }, [featureToggles, hasPermission])

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", JSON.stringify(collapsed))
  }, [collapsed])

  // Get selected menu key based on current route
  const getSelectedKey = (): string[] => {
    const key = getMenuKeyFromPathname(location.pathname)
    return key ? [key] : []
  }

  // Handle menu item click
  const handleMenuClick = ({ key }: { key: string }) => {
    // Find the route path from the key
    const routePath = Object.entries(ROUTE_TO_KEY_MAP).find(
      ([_, menuKey]) => menuKey === key
    )?.[0]

    if (routePath) {
      navigate({ to: routePath })
    }

    onMobileClose?.()
  }

  const navigationContent = (
    <>
      {/* Organization Selector at the top */}
      <div style={{ padding: collapsed && !isMobile ? "16px 8px" : "16px" }}>
        <OrganizationSelector style={{ width: "100%" }} />
      </div>

      {/* Navigation Menu */}
      <Menu
        mode="inline"
        selectedKeys={getSelectedKey()}
        items={filteredItems}
        onClick={handleMenuClick}
        style={{ paddingBottom: "48px" }}
      />

      {/* Reserves clearance below the last menu item so it never sits under the collapse trigger */}
      {!isMobile && <div style={{ height: 53 }} aria-hidden="true" />}
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        // antd v6 deprecated `width` on Drawer in favor of `size`, which accepts
        // a pixel value directly (not just the "default"/"large" presets)
        size={280}
        open={mobileOpen}
        onClose={onMobileClose}
        closable
        styles={{ body: { padding: 0 } }}
      >
        {navigationContent}
      </Drawer>
    )
  }

  return (
    <Sider
      width={280}
      theme={isDark ? "dark" : "light"}
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      reverseArrow
      style={{
        overflow: "auto",
        height: "100%",
      }}
    >
      {navigationContent}
    </Sider>
  )
}
